/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { InMemoryStorage, getConsentStatus as getSharedConsentStatus, snapshotStorage } from './in-memory-storage';

/**
 * Original sessionStorage implementation.
 * We keep a reference to the original to use when consent is given.
 */
export const originalSessionStorage = window.sessionStorage;

let inMemorySessionStorage: InMemoryStorage | null = null;

/**
 * Returns the singleton in-memory sessionStorage instance.
 */
export function getInMemorySessionStorage(): InMemoryStorage {
  if (!inMemorySessionStorage) {
    inMemorySessionStorage = new InMemoryStorage();
  }
  return inMemorySessionStorage;
}

/**
 * Gets consent status from cookies or in-memory storage.
 * This is a lightweight check that doesn't require React context.
 *
 * @returns {string} Consent status: 'pending', 'accepted', or 'rejected'
 */
function getConsentStatus(): 'pending' | 'accepted' | 'rejected' {
  return getSharedConsentStatus(() => localStorage.getItem('cdu_consent_v1'));
}

/**
 * Checks if consent is accepted.
 *
 * @returns {boolean} True if consent is accepted
 */
export function hasConsent(): boolean {
  return getConsentStatus() === 'accepted';
}

/**
 * Migrates data from in-memory session storage to real sessionStorage when consent is accepted.
 *
 * @returns {void}
 */
export function migrateInMemoryToSessionStorage(): void {
  console.log('[SessionStorageWrapper] Migrating in-memory data to sessionStorage');

  const memory = getInMemorySessionStorage();

  const keys: string[] = [];
  for (let i = 0; i < memory.length; i++) {
    const key = memory.key(i);
    if (key) {
      keys.push(key);
    }
  }

  keys.forEach((key) => {
    try {
      const value = memory.getItem(key);
      if (value !== null) {
        originalSessionStorage.setItem(key, value);
        console.log(`[SessionStorageWrapper] Migrated key: ${key}`);
      }
    } catch (error) {
      console.error(`[SessionStorageWrapper] Error migrating key ${key}:`, error);
    }
  });

  memory.clear();
  console.log('[SessionStorageWrapper] In-memory data migration complete');
}

/**
 * Clears the in-memory session storage.
 *
 * @returns {void}
 */
export function clearInMemorySessionStorage(): void {
  getInMemorySessionStorage().clear();
  console.log('[SessionStorageWrapper] In-memory session storage cleared');
}

/**
 * Gets all keys and values from in-memory sessionStorage.
 * Used for debugging purposes.
 *
 * @returns {Record<string, string>} Object with all in-memory sessionStorage data
 */
export function getInMemorySessionStorageContents(): Record<string, string> {
  return snapshotStorage(getInMemorySessionStorage());
}
